import React, {FC} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {Loader} from '@optimaxdev/design-system';
import {Icon, KlarnaBadge} from '@optimaxdev/icons';
import {connect} from 'react-redux';

import {CHECKOUT_PAYMENT_STEP} from 'constants/analytics/events/checkout';
import {ApplicationStoreType} from 'desktop/store';
import {PaymentType} from 'types/PaymentMethodsType';

import {STEPS} from '../../constants/checkout';
import {PAYMENTS} from '../../constants/paymentStep';
import {
    KlarnaContext,
    KlarnaPropsType,
    KlarnaProvider,
} from '../../contexts/klarnaProvider/klarnaProvider';
import {
    useGetBillingAddressForKlarna,
    useGetShippingAddressForKlarna,
} from '../../hooks/useGetAddressForKlarna/useGetAddressForKlarna';
import {setOptionPaymentMethod as setOptionPaymentMethodAction} from '../../store/reducers/analytics/analytics';
import {sendPayerInfoToSaga} from '../../store/reducers/paymentStep/payerInfo/payerInfo';
import {getCurrentStep} from '../../store/selectors/currentStep/currentStep';
import {getTotalsForKlarna} from '../../store/selectors/klarna/klarna';
import {
    getIsSelectedByKey,
    getPaymentMethodByKey,
} from '../../store/selectors/paymentStep/paymentStep';
import {PaymentWrapperConnected} from '../paymentWrapper/paymentWrapper';
import {PlaceOrderForKlarnaConnected} from '../placeOrderForKlarna/placeOrderForKlarna';

import s from './paymentKlarna.scss';

type KlarnaTitlePropsType = {
    isLoading: boolean;
    name?: Maybe<string>;
};

export type PropsType = {
    method: PaymentType;
    isSelected: boolean;
    runSavePayment: (data: {paymentId: string; token: string; method: string}) => void;
    setOptionPaymentMethod: () => void;
    isPaymentStep: boolean;
} & Omit<KlarnaPropsType, 'onAuthorizeCallback' | 'billingAddress' | 'shippingAddress'>;

/**
 * Component for display title and badge
 * @param {KlarnaTitlePropsType} props - current props
 * @return {JSX.Element} component
 */
const KlarnaTitle: FC<KlarnaTitlePropsType> = ({isLoading, name}) => {
    if (isLoading) return <Loader />;

    return (
        <div className={s.title}>
            <Icon height={28} icon={KlarnaBadge} width={67} />
            {name && <span className={s.name}>{name}</span>}
        </div>
    );
};

/**
 * Component for payment by klarna
 * @param {PropsType} props - current props
 * @return {React.Element} component
 */
export const PaymentKlarna: FC<PropsType> = ({
    amount,
    isPaymentStep,
    isSelected,
    method: {code},
    orderLines,
    orderTaxAmount,
    runSavePayment,
    setOptionPaymentMethod,
}) => {
    const shippingAddress = useGetShippingAddressForKlarna();
    const billingAddress = useGetBillingAddressForKlarna();

    return (
        <KlarnaProvider
            amount={amount}
            billingAddress={billingAddress}
            isPaymentStep={isPaymentStep}
            onAuthorizeCallback={runSavePayment}
            orderLines={orderLines}
            orderTaxAmount={orderTaxAmount}
            shippingAddress={shippingAddress}
        >
            <KlarnaContext.Consumer>
                {({anchor, authorize, isEmptyCategories, isError, isLoading}) => {
                    if (isError || isEmptyCategories) return null;

                    const onClickContinue = () => {
                        sendDesktopAnalyticsEvent(CHECKOUT_PAYMENT_STEP, {
                            message: 'CTA - Klarna',
                            method: 'Payment',
                        });
                        setOptionPaymentMethod();
                        authorize();
                    };

                    return (
                        <PaymentWrapperConnected
                            code={code}
                            isSelected={isSelected}
                            title={<KlarnaTitle isLoading={isLoading} />}
                        >
                            {anchor}
                            <PlaceOrderForKlarnaConnected
                                isLoading={isLoading}
                                onSubmit={onClickContinue}
                            />
                        </PaymentWrapperConnected>
                    );
                }}
            </KlarnaContext.Consumer>
        </KlarnaProvider>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => {
    const {amount, orderLines, orderTaxAmount} = getTotalsForKlarna(store);

    return {
        amount,
        isPaymentStep: getCurrentStep(store) === STEPS.PAYMENT_STEP,
        isSelected: getIsSelectedByKey(store, PAYMENTS.METHOD_KLARNA),
        method: getPaymentMethodByKey(store, PAYMENTS.METHOD_KLARNA),
        orderLines,
        orderTaxAmount,
    };
};

const mapDispatchToProps = {
    runSavePayment: sendPayerInfoToSaga,
    setOptionPaymentMethod: setOptionPaymentMethodAction,
};

export const PaymentKlarnaConnected = connect(mapStateToProps, mapDispatchToProps)(PaymentKlarna);
